import { usePush } from '@remix-pwa/push/client'
import { Button, Flex, message, notification, Typography } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import { useUserContext } from '~/core/context'
import { Api } from '~/core/trpc'
import { ButtonCTA } from '~/features/shared/components'
import { Icon } from '~/features/shared/icons'
import { MixpanelClient } from '~/plugins/mixpanel/client'
import { usePwaContext } from '../../context'

const Internal: React.FC = () => {
  const mixpanel = MixpanelClient.use()

  const { session } = useUserContext()

  const [api, contextHolder] = notification.useNotification()

  const [isOpen, setOpen] = useState(false)
  const [isLoading, setLoading] = useState(false)

  const { mutateAsync: saveSubscription } = Api.pwa.onSubscribe.useMutation()

  const timeoutRef = useRef(null)

  const { subscribeToPush, isSubscribed, requestPermission } = usePush()

  const { isRunning, publicKey } = usePwaContext()

  const handleClose = () => {
    api.destroy()
    setOpen(false)
  }

  const handleRefuse = () => {
    mixpanel.track('pwa.notifications.refused')

    handleClose()
  }

  const subscribe = () => {
    setLoading(true)

    mixpanel.track('pwa.notifications.accepted')

    subscribeToPush(publicKey, async subscription => {
      try {
        await saveSubscription({ subscription: JSON.stringify(subscription) })

        message.success(`Notifications activated`)
      } catch (error) {
        message.error(`Could not activate notifications: ${error.message}`)
      }

      setLoading(false)

      handleClose()
    })
  }

  useEffect(() => {
    if (!session.isLoggedIn) {
      return
    }

    if (isRunning) {
      const status = requestPermission()

      if (isSubscribed || status === 'denied') {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = null
      } else {
        timeoutRef.current = setTimeout(() => {
          setOpen(true)
        }, 2000)
      }
    }
  }, [isRunning, isSubscribed, session.isLoggedIn])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    api.open({
      message: '',
      description: (
        <Flex vertical align="center" style={{ marginRight: '36px' }}>
          <Typography.Title
            style={{ textAlign: 'center', margin: 0 }}
            level={3}
          >
            <Icon name="bell-alert" />
          </Typography.Title>
          <Typography.Title style={{ textAlign: 'center' }} level={3}>
            Stay connected
          </Typography.Title>

          <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
            Enable notifications to never miss a message from your AI Employees.
          </Typography.Text>

          <Flex vertical gap={8} className="mt-5">
            <ButtonCTA
              type="primary"
              block
              onClick={subscribe}
              loading={isLoading}
            >
              Enable Notifications
            </ButtonCTA>
            <Button block onClick={handleRefuse} disabled={isLoading}>
              Maybe later
            </Button>
          </Flex>
        </Flex>
      ),
      placement: 'top',
      duration: 0,
      icon: <></>,
      key: 'popup-notification',
      onClose: handleRefuse,
    })
  }, [isOpen])

  return <>{contextHolder}</>
}

export const NotificationPopup: React.FC = () => {
  const ctx = usePwaContext()

  if (!ctx.isRunning || !ctx.isNotificationSupported) {
    return <></>
  }

  return <Internal />
}
